<template>
  <modal
    v-bind="$attrs"
    v-on="$listeners"
    :errors="{}"
  >
    <template v-slot:body>
      <el-checkbox 
        class="resend-all capitalize" 
        :indeterminate="isIndeterminate" 
        v-model="checkAll" 
        @change="handleCheckAllChange"
        >
        {{ $t('common.select_all') }}
      </el-checkbox>
      <el-checkbox-group v-model="checkedPendingInvite" @change="handleCheckedPendingInviteChange">
        <ul class="user-list">
          <li
            class="user-wrapper"
            v-for="(invitedUser, idx) in pendingInviteUsers"
            :key="idx"
          >
            <el-checkbox :label="invitedUser" :key="idx">
              <span
                v-if="invitedUser.first_name && invitedUser.last_name"
                class="name" >
                {{ invitedUser.first_name }} {{ invitedUser.last_name }}
              </span>
              <span v-else class="email">
                {{ invitedUser.email }}
              </span>
            </el-checkbox>
            <span class="created">
              {{ parseDate(invitedUser.created) }}
            </span>
            <span
              class="resend-invite"
              @click="resendPendingInvite(invitedUser.id)"
            >
              <chip>
                {{ $t('common.resend_invite') }}
              </chip>
            </span>
            <span
              class="remove-invite"
              @click="removePendingInvite(invitedUser.id)"
              :title="$t('common.remove_invite')"
              :data-index="idx"
            >
              <i class="elsa-icon-grey-delete" />
            </span>
          </li>
        </ul>
      </el-checkbox-group>
      <pagination
        :page-size="maxNumberOfElements"
        :total="total"
        :pager-counter="5"
        :current-page.sync="page"
      />
      <div class="actions">
        <primary-button
          @click.native="resendCheckedPendingInvite"
        >
          {{ $t('common.resend_invite') }}
        </primary-button>
      </div>
    </template>
  </modal>
</template>

<script>
  import Modal from '@/components/common/Modal'
  import Chip from '@/components/common/Chip'
  import Pagination from '@/components/common/Pagination'
  import { mapActions, mapGetters } from 'vuex'
  import { successMessage, warningMessage } from '../../utils/messages'
  import { formatDistanceToNow, isDate } from 'date-fns'
  import { always, map, pathOr, isEmpty } from 'ramda'
  import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER } from '../../constants/generic'
  import PrimaryButton from '@/components/common/buttons/PrimaryButton'

  export default {
    components: {
      Modal,
      Chip,
      Pagination,
      PrimaryButton
    },
    data() {
      return {
        checkAll: false,
        checkedPendingInvite: [],
        isIndeterminate: true,
      }
    },
    props: {
      pendingInviteUsers: {
        type: Array,
        default: () => []
      },
      currentPage: {
        default: 1,
      },
      total: {
      },
      pendingInviteTypeScope: {
        type: String,
        default: 'class'
      }
    },
    methods: {
      closeModal() {
        return this.$emit('closed')
      },
      resendPendingInvite(inviteId) {
        return this.resendInvite(inviteId)
          .then(() => {
            return successMessage(this.$t('organization_profile.invite_admin_modal.invite_success'))(this)
          })
          .catch(() => {
            return warningMessage(this.$t('common.invite_send_not_success'))(this)
          })
      },
      removePendingInvite(inviteId) {
        this.$confirm(this.$t('organization_profile.invite_admin_modal.confirm_remove_invitation'), 'Confirm', {
            confirmButtonText: 'YES',
            cancelButtonText: 'NO',
            type: 'warning'
          }).then(() => {
            return this.handleRemoveInvite(inviteId)
              .then(() => {
                return successMessage(this.$t('organization_profile.invite_admin_modal.remove_invite_success'))(this)
              })
              .catch(() => {
                return warningMessage(this.$t('organization_profile.invite_admin_modal.remove_invite_fail'))(this)
              })
          }).catch(() => {
            return
          });
      },
      resendCheckedPendingInvite(){
        const inviteIds = map(pathOr('',["id"]), this.checkedPendingInvite);
        if(isEmpty(inviteIds)){
          return warningMessage(this.$t('common.no_invite_seleted'))(this)
        }
        return this.resendMultiInvite({
          organizationId: this.currentOrganizationId,
          inviteIds: inviteIds})
          .then(() => {
            return successMessage(this.$t('organization_profile.invite_admin_modal.invite_success'))(this)
          })
          .catch(() => {
            return warningMessage(this.$t('common.invite_send_not_success'))(this)
          })
      },
      parseDate(date) {
        if (!isDate(date)) return ' - '

        return formatDistanceToNow(date, { addSuffix: true })
      },
      ...mapActions({
        resendInvite: 'invite/resendInvite',
        removeInviteClazz: 'classes/removeInvite',
        removeInviteOrg: 'organization/removeInvite',
        resendMultiInvite: 'invite/resendMultiInvite'
      }),
      handleCheckedPendingInviteChange(value) {
        let checkedCount = value.length;
        this.checkAll = checkedCount === this.pendingInviteUsers.length;
        this.isIndeterminate = checkedCount > 0 && checkedCount < this.pendingInviteUsers.length;
      },
      handleCheckAllChange(val) {
        this.checkedPendingInvite = val ? this.pendingInviteUsers : [];
        this.isIndeterminate = false;
      },
      handleRemoveInvite(inviteId) {
        if (this.pendingInviteTypeScope == "class") {
          return this.removeInviteClazz(inviteId)
        } else {
          return this.removeInviteOrg(inviteId)
        }
      }
    },
    computed: {
      maxNumberOfElements: always(NUMBER_ELEMENTS_PER_PAGE),
      pageCounter: always(PAGE_COUNTER),
      page: {
        get() { 
          return this.currentPage },
        set(newPage) { return this.$emit('update:currentPage', newPage) }
      },
      ...mapGetters({
        currentOrganizationId: 'organization/currentOrganizationId'
      }),
    },
  }
</script>

<style lang="scss" scoped>
  ::v-deep .el-dialog.elsa-modal {
    background-color: #F2F7FA;
    width: 50%;
  }
  ul {
    list-style: none;
    padding: 0;
    li {
      background: #F9FBFD;
      padding: 0 24px;
      margin-bottom: 4px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 55px;
      &:hover img {
        display: block;
      }
    }
    .resend-invite{
      padding-right: 15px;
    }
    .remove-invite{
      padding-right: 15px;
      cursor: pointer;
    }
  }
  .actions {
    align-items: flex-start;
    .el-button {
      padding: 12px;
    }
  }

  .user-wrapper .created {
    flex: 1;
    text-align: end;
    margin: auto 10px;
  }

  ::v-deep .el-dialog__body .el-pagination {
    flex-direction: row !important;
  }
  .resend-all{
    display: inline !important;
    padding-bottom: 10px !important;
    padding-left: 24px !important;
  }
</style>
